import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { getFirebaseServices } from '@/lib/firebase/client';
import { BarChart3, Wallet, Gamepad2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';

export default function HomePage() {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [videos, setVideos] = useState<string[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videoReady, setVideoReady] = useState<boolean[]>([]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId = 0 as number;
    let scrollPosition = scrollContainer.scrollLeft || 0;
    let lastTimestamp = performance.now();
    const speedPxPerSec = 30; // pixels per second

    const animate = (t: number) => {
      if (!scrollContainer) return;
      const delta = Math.max(0, t - lastTimestamp);
      lastTimestamp = t;

      scrollPosition += (speedPxPerSec * delta) / 1000;
      const half = scrollContainer.scrollWidth / 2 || Number.MAX_SAFE_INTEGER;
      if (scrollPosition >= half) scrollPosition = 0;
      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setVideosLoading(true);
        const res = await fetch('/videos/videos.json');
        if (res.ok) {
          const data = await res.json();
          if (!mounted || !data) return;
          const urls = (data.videos || []).map((f: string) => {
            if (typeof f === 'string' && /^https?:\/\//i.test(f)) return f;
            return `/videos/${encodeURIComponent(String(f))}`;
          });
          setVideos(urls);
        }
      } catch (err) {
        console.error('Failed to fetch videos list', err);
      } finally {
        if (mounted) setVideosLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    // initialize ready flags for each unique video
    setVideoReady(new Array(videos.length).fill(false));
  }, [videos]);

  const markVideoReady = (index: number) => {
    setVideoReady((prev) => {
      const next = prev ? [...prev] : [];
      if (index >= 0 && index < videos.length) next[index] = true;
      return next;
    });
  };

  const goToAuth = async (target: string) => {
    const services = getFirebaseServices();
    const isSignedIn = !!(services && services.auth && services.auth.currentUser);
    if (isSignedIn) {
      navigate('/dashboard');
      return;
    }
    navigate(target);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-zinc-950">
      {/* Geometric Background Shapes */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-10 top-20 h-32 w-32 rotate-45 rounded-3xl bg-pink-400/20"></div>
        <div className="absolute right-20 top-40 h-24 w-24 rounded-full bg-cyan-400/20"></div>
        <div className="absolute bottom-40 left-1/4 h-40 w-40 rotate-12 bg-yellow-300/20" style={{ clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)" }}></div>
        <div className="absolute bottom-20 right-1/3 h-28 w-28 rounded-2xl bg-purple-400/20"></div>
        <div className="absolute left-1/2 top-1/3 h-36 w-36 -rotate-12 rounded-full bg-lime-400/20"></div>
        <div className="absolute bottom-1/4 right-10 h-20 w-20 rotate-45 bg-orange-400/20"></div>
      </div>

      {/* Header with Funky Broken Shape */}
      <header className="relative z-40 bg-black/25 backdrop-blur">
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400"></div>
        <div className="absolute -bottom-2 left-20 h-3 w-32 bg-pink-400/60"></div>
        <div className="absolute -bottom-3 left-60 h-4 w-24 bg-purple-400/60"></div>
        <div className="absolute -bottom-2 right-40 h-3 w-40 bg-cyan-400/60"></div>
        <div className="absolute -bottom-4 right-10 h-5 w-20 bg-pink-400/60"></div>
        
        <div className="absolute -top-3 left-1/4 h-6 w-6 rotate-45 bg-yellow-400/40"></div>
        <div className="absolute -top-2 right-1/3 h-5 w-5 rounded-full bg-cyan-400/40"></div>
        
        <div className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10">
              <img
                src="/logos/LTE.png"
                alt="LTS Logo"
                width={40}
                height={40}
                className="object-contain"
              />
            </div>
            <span className="text-xl font-black tracking-tight text-white">LEMON TREE AFFILIATE</span>
          </div>
          
          <button
            onClick={() => goToAuth('/auth/login')}
            className="group relative overflow-visible px-5 py-2.5 text-sm font-bold text-zinc-950 shadow-lg transition hover:scale-105"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400"></div>
            <div className="absolute -left-1 top-1 h-3 w-3 bg-pink-400"></div>
            <div className="absolute -right-1 bottom-1 h-4 w-4 bg-cyan-400"></div>
            <div className="absolute -top-1 right-1/3 h-2 w-6 bg-purple-400"></div>
            <span className="relative z-10">Login / Sign up</span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative z-10">
        <section className="mx-auto max-w-5xl px-4 py-24 text-center">
          <div className="mb-8 inline-block rounded-2xl bg-purple-500/20 px-4 py-2 text-sm font-bold uppercase tracking-wider text-purple-300">
            Faceless Creator Program
          </div>
          <h1 className="mb-6 text-6xl font-black leading-tight text-white md:text-7xl">
            Earn Money
            <br />
            <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              With Every Post
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-zinc-400">
            Use our gameplay for your posts Instagram, TikTok, YouTube & Snapchat. Get paid for every install from your custom link in bio.
          </p>
          
          <button
            onClick={() => goToAuth('/auth/signup')}
            className="group relative inline-block overflow-visible px-10 py-4 text-lg font-black text-zinc-950 shadow-2xl transition hover:scale-105"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400"></div>
            <div className="absolute -left-2 -top-2 h-6 w-6 bg-pink-400"></div>
            <div className="absolute -right-2 -bottom-2 h-8 w-8 bg-cyan-400"></div>
            <div className="absolute -top-3 right-1/4 h-4 w-12 bg-purple-400"></div>
            <div className="absolute -bottom-1 left-1/4 h-3 w-8 bg-pink-400"></div>
            <span className="relative z-10">Start Earning Now</span>
          </button>
        </section>

        {/* Video Carousel */}
        <section className="relative overflow-hidden py-16">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-black text-white">See It In Action</h2>
            <p className="mt-2 text-zinc-400">Our gameplay, your creativity</p>
          </div>
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-hidden px-4"
            style={{ scrollBehavior: 'auto' }}
          >
            {videosLoading && (
              <>
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={`skeleton-${i}`}
                    className="relative flex-shrink-0 overflow-hidden rounded-2xl border-4 border-pink-400/30 shadow-xl"
                    style={{ width: '280px', height: '500px' }}
                  >
                    <Skeleton className="h-full w-full" />
                  </div>
                ))}
              </>
            )}
            {!videosLoading && [...videos, ...videos].map((src, i) => {
              const originalIndex = videos.length ? i % videos.length : i;
              const isReady = !!videoReady[originalIndex];
              return (
                <div
                  key={i}
                  className="relative flex-shrink-0 overflow-hidden rounded-2xl border-4 border-pink-400/30 shadow-xl"
                  style={{ width: '280px', height: '500px' }}
                >
                  {!isReady && (
                    <div className="absolute inset-0 z-20">
                      <Skeleton className="h-full w-full" />
                    </div>
                  )}
                  <video
                    src={src}
                    preload="metadata"
                    autoPlay
                    muted
                    loop
                    playsInline
                    onLoadedData={() => markVideoReady(originalIndex)}
                    className="h-full w-full object-cover"
                  />
                </div>
              );
            })}
          </div>
        </section>

        {/* Features Section */}
        <section className="mx-auto max-w-6xl px-4 py-20">
          <div className="grid gap-8 md:grid-cols-3">
            <div className="group relative overflow-hidden rounded-3xl border-4 border-cyan-400/40 bg-zinc-900 p-8 transition hover:scale-105">
              <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-cyan-400/20"></div>
              <div className="relative">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500">
                  <BarChart3 className="h-8 w-8 text-zinc-950" strokeWidth={3} />
                </div>
                <h3 className="mb-3 text-2xl font-black text-white">Track Installs</h3>
                <p className="text-zinc-400">
                  Real-time dashboard shows every install, earnings breakdown, and campaign performance metrics.
                </p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl border-4 border-purple-400/40 bg-zinc-900 p-8 transition hover:scale-105">
              <div className="absolute -left-8 -top-8 h-32 w-32 rotate-45 bg-purple-400/20"></div>
              <div className="relative">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500">
                  <Wallet className="h-8 w-8 text-zinc-950" strokeWidth={3} />
                </div>
                <h3 className="mb-3 text-2xl font-black text-white">Get Paid Fast</h3>
                <p className="text-zinc-400">
                  Request PayPal payouts at $50. Country-based CPI rates ensure fair pay for creators worldwide.
                </p>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl border-4 border-pink-400/40 bg-zinc-900 p-8 transition hover:scale-105">
              <div className="absolute -bottom-8 -right-8 h-28 w-28 rounded-2xl bg-pink-400/20"></div>
              <div className="relative">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-pink-400 to-orange-500">
                  <Gamepad2 className="h-8 w-8 text-zinc-950" strokeWidth={3} />
                </div>
                <h3 className="mb-3 text-2xl font-black text-white">3-Slot System</h3>
                <p className="text-zinc-400">
                  Promote up to 3 games per social account. Swap campaigns anytime and download gameplay footage instantly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative mx-auto max-w-4xl px-4 py-24 text-center">
          <div className="absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-purple-500/10 blur-3xl"></div>
          <div className="relative">
            <h2 className="mb-4 text-5xl font-black text-white">
              Ready to Get Started?
            </h2>
            <p className="mb-10 text-xl text-zinc-400">
              Create your account and start earning in under 60 seconds
            </p>
            
            <button
              onClick={() => goToAuth('/auth/signup')}
              className="group relative inline-block overflow-visible bg-zinc-950 px-10 py-4 text-lg font-black text-cyan-400 shadow-xl transition hover:text-pink-400"
            >
              <div className="absolute inset-0 border-4 border-cyan-400"></div>
              <div className="absolute -left-1 -top-1 h-5 w-5 border-l-4 border-t-4 border-pink-400"></div>
              <div className="absolute -right-1 -bottom-1 h-6 w-6 border-b-4 border-r-4 border-purple-400"></div>
              <div className="absolute -top-2 right-1/3 h-3 w-16 border-t-4 border-cyan-400"></div>
              <div className="absolute -bottom-2 left-1/4 h-4 w-12 border-b-4 border-pink-400"></div>
              <span className="relative z-10">Join Now – It's Free</span>
            </button>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-zinc-900 py-8 text-center">
        <p className="text-sm font-semibold text-zinc-500">
          &copy; 2025 Lemon Tree Studio. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
